// Loyalty system types

export interface LoyaltyTier {
  id: string;
  name: string;
  description: string;
  minimum_points: number;
  maximum_points: number | null;
  badge_url?: string;
  color_hex: string;
  created_at: string;
  updated_at: string;
}

export interface LoyaltyPointsTransaction {
  id: string;
  user_id: string;
  points_earned: number;
  points_spent: number;
  transaction_type: string;
  order_id?: string;
  created_at: string;
  expires_at?: string;
  order_status?: string;
  venue_name?: string;
  venue_id?: string;
}

export interface LoyaltySummary {
  total_points_earned: number;
  current_points: number;
  tier_name: string;
  tier_description: string;
  tier_color_hex: string;
  next_tier_name: string | null;
  next_tier_minimum_points: number | null;
  points_to_next_tier: number | null;
}

// Helper functions

/**
 * Format points with commas for thousands separators
 */
export const formatPoints = (points: number | null | undefined): string => {
  if (points === null || points === undefined) return '0';
  return points.toLocaleString('en-US');
};

/**
 * Calculate percentage progress to next tier
 */
export const calculateTierProgress = (
  currentPoints: number | null | undefined,
  currentTierMinimum: number | null | undefined,
  nextTierMinimum: number | null | undefined
): number => {
  // Handle null/undefined cases
  if (currentPoints === null || currentPoints === undefined) currentPoints = 0;
  if (currentTierMinimum === null || currentTierMinimum === undefined) currentTierMinimum = 0;
  if (!nextTierMinimum) return 100; // At max tier
  
  const pointsInCurrentTier = currentPoints - currentTierMinimum;
  const totalPointsInTier = nextTierMinimum - currentTierMinimum;
  
  // Prevent division by zero
  if (totalPointsInTier <= 0) return 0; 
  
  const progress = (pointsInCurrentTier / totalPointsInTier) * 100;
  // Handle NaN, negative values, and ensure between 0-100
  if (isNaN(progress)) return 0;
  return Math.min(Math.max(0, progress), 100);
};

/**
 * Format transaction type for display
 */
export const formatTransactionType = (type: string | null | undefined): string => {
  if (!type) return 'Unknown';
  const formattedType = type.replace(/_/g, ' ');
  return formattedType.charAt(0).toUpperCase() + formattedType.slice(1);
};

/**
 * Get a short display string for transaction type
 */
export const getTransactionLabel = (transaction: LoyaltyPointsTransaction): string => {
  if (!transaction || !transaction.transaction_type) {
    return 'Unknown transaction';
  }
  
  switch (transaction.transaction_type) {
    case 'order_completed':
      return transaction.venue_name 
        ? `Order at ${transaction.venue_name}` 
        : 'Order completed';
    default:
      return formatTransactionType(transaction.transaction_type);
  }
};